import { c as _c } from "react/compiler-runtime";
import { postOrgGraphsByGraphIdNodesByNodeIdValidateExpressions, postOrgGraphsByGraphIdNodesByNodeIdValidateExpressionsQueryKey } from '@onefootprint/fetch-dashboard';
import type { CelProgram, TypedOnboardingData } from '@onefootprint/request-types/dashboard';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounceValue } from 'usehooks-ts';
import useStore from './use-store';
const DEBOUNCE_MS = 1000;
const EMPTY: Map<string, TypedOnboardingData> = new Map();

/**
 * A half-written row isn't sent to be typed: an empty expression would come back as an error the
 * user hasn't caused, and a program with no key can't be matched to its row. Rows that fail this
 * have no type coming, so a form shouldn't report one as pending either.
 */
export const isTypeable = ({
  dataKey,
  expression
}: {
  dataKey?: string;
  expression?: string;
}): boolean => !!dataKey?.trim() && !!expression?.trim();

/**
 * The type each of a node's CEL programs resolves to, resolved without saving them and keyed by the
 * data key the form holds. Empty until the first response, and holds the last resolved types while
 * an edit is in flight.
 *
 * Every program is sent on each request, not just the edited one: an expression node types each
 * program against the keys the earlier ones assign, so a partial list types them wrong.
 */
export const useValidateExpressions = (nodeId, programs) => {
  const $ = _c(13);
  const {
    data: t0
  } = useStore();
  const {
    graph
  } = t0;
  let t1;
  if ($[0] !== programs) {
    t1 = programs.filter(isTypeable);
    $[0] = programs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      equalityFn: _temp
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [debounced] = useDebounceValue(t1, DEBOUNCE_MS, t2);
  let t3;
  let t4;
  let t5;
  if ($[3] !== debounced || $[4] !== graph.id || $[5] !== nodeId) {
    const options = {
      path: {
        graphId: graph.id,
        nodeId
      },
      body: {
        programs: debounced
      }
    };
    t4 = useQuery;
    t3 = postOrgGraphsByGraphIdNodesByNodeIdValidateExpressionsQueryKey(options);
    t5 = async t6 => {
      const {
        queryKey,
        signal
      } = t6;
      const {
        data
      } = await postOrgGraphsByGraphIdNodesByNodeIdValidateExpressions({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true
      });
      return new Map(queryKey[0].body.programs.flatMap((t7, i) => {
        const {
          dataKey
        } = t7;
        const typed = data.outputs[i];
        return typed ? [[dataKey, typed] as const] : [];
      }));
    };
    $[3] = debounced;
    $[4] = graph.id;
    $[5] = nodeId;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  const t6 = !!graph.id && debounced.length > 0;
  let t7;
  if ($[9] !== t3 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t5,
      enabled: t6,
      placeholderData: keepPreviousData
    };
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: data_0
  } = t4(t7);
  return data_0 ?? EMPTY;
};
export default useValidateExpressions;
function _temp(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}